import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import ExcalidrawComponent from '../ExcalidrawComponent';

// Base container for the Excalidraw block
const ExcalidrawBlockContainer = styled.div`
  position: relative;
  margin: 16px 0;
  padding: 0;
  border-radius: 6px;
  background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'transparent'};
  border: 1px solid ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : '#e0e0e0'};
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'var(--color-hover-bg, rgba(0, 0, 0, 0.02))'};
    border-color: ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : '#c0c0c0'};
  }

  ${props => props.customStyles}
`;

// Excalidraw editor container
const ExcalidrawEditorContainer = styled.div`
  width: 100%;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  white-space: pre-wrap;
  padding: 12px;
  outline: none;
`;

// Excalidraw view container
const ExcalidrawViewContainer = styled.div`
  width: 100%;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  .excalidraw-container {
    padding: 16px;
    text-align: center;
    width: 100%;
    height: 100%;
    min-height: 300px;
  }
  
  .excalidraw-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    width: 100%;
    color: #6c757d;
    font-style: italic;
    background-color: #f8f9fa;
    border-radius: 4px;
  }
`;

// Helper function to extract excalidraw ref ID from content
const extractExcalidrawRef = (content) => {
  if (!content) return null;
  
  // Handle both string and array content
  const contentStr = Array.isArray(content) ? content.join('\n') : content;
  
  // Find the reference ID in the content
  const refMatch = contentStr.match(/ref="([^"]+)"/);
  return refMatch ? refMatch[1] : null;
};

// Helper function to create excalidraw markdown
const createExcalidrawMarkdown = (refId) => {
  if (!refId) return '';
  
  return [
    '```excalidraw',
    `ref="${refId}"`,
    '```'
  ].join('\n');
};

/**
 * ExcalidrawBlock component for rendering and editing Excalidraw diagrams
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isActive Whether the block is active/being edited
 * @param {string|Array} props.content Excalidraw block content (markdown format)
 * @param {Function} props.onClick Handler for click events
 * @param {Function} props.onContentChange Callback when content changes
 * @param {Function} props.commitContent Callback to commit content changes
 * @param {Object} props.customStyles Custom styles for the container
 */
const ExcalidrawBlock = React.forwardRef(({
  isActive = false,
  content = '',
  onClick,
  onContentChange,
  commitContent,
  customStyles = {},
  ...otherProps
}, ref) => {
  const editorRef = useRef(null);
  const [initialContent, setInitialContent] = useState(
    Array.isArray(content) ? content.join('\n') : content
  );
  const [isEditing, setIsEditing] = useState(isActive);
  const prevIsActiveRef = useRef(isActive);
  
  // Extract the excalidraw reference ID
  const excalidrawRef = useMemo(() => {
    return extractExcalidrawRef(content);
  }, [content]);
  
  // Update content when it changes from parent
  useEffect(() => {
    const contentStr = Array.isArray(content) ? content.join('\n') : content;
    
    // Only update if not actively editing
    if (!isEditing) {
      setInitialContent(contentStr);
    }
  }, [content, isEditing]);
  
  // Handle active state changes
  useEffect(() => {
    // Handle becoming active
    if (isActive && !prevIsActiveRef.current) {
      setIsEditing(true);
      
      // Focus the editor when it becomes active
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          
          // Place cursor at end
          const selection = window.getSelection();
          const range = document.createRange();
          
          if (editorRef.current.lastChild) {
            range.setStartAfter(editorRef.current.lastChild);
          } else {
            range.setStart(editorRef.current, 0);
          }
          
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
    }
    
    // Handle becoming inactive
    if (!isActive && prevIsActiveRef.current) {
      // Commit content when going inactive
      if (editorRef.current && commitContent) {
        const currentContent = editorRef.current.innerText;
        if (currentContent !== initialContent) {
          commitContent(currentContent);
          setInitialContent(currentContent);
        }
      }
      setIsEditing(false);
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, initialContent, commitContent]);
  
  // Handle input changes
  const handleInput = useCallback(() => {
    if (editorRef.current && onContentChange) {
      const currentContent = editorRef.current.innerText;
      onContentChange(currentContent);
    }
  }, [onContentChange]);
  
  // Handle blur events
  const handleBlur = useCallback(() => {
    if (editorRef.current && commitContent) {
      const currentContent = editorRef.current.innerText;
      if (currentContent !== initialContent) {
        commitContent(currentContent);
        setInitialContent(currentContent);
      }
    }
    setIsEditing(false);
  }, [initialContent, commitContent]);
  
  // Handle key presses
  const handleKeyDown = useCallback((e) => {
    // Handle ESC - revert changes
    if (e.key === 'Escape' && editorRef.current && initialContent !== editorRef.current.innerText) {
      e.preventDefault();
      editorRef.current.innerText = initialContent;
      
      if (onContentChange) {
        onContentChange(initialContent);
      }
      
      setIsEditing(false);
      return;
    }
  }, [initialContent, onContentChange]);
  
  // Render the component
  return (
    <ExcalidrawBlockContainer
      isActive={isActive}
      customStyles={customStyles}
      ref={ref}
      {...otherProps}
    >
      {isEditing ? (
        <ExcalidrawEditorContainer
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: initialContent }}
          onInput={handleInput}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <ExcalidrawViewContainer onClick={onClick}>
          {excalidrawRef ? (
            <div className="excalidraw-container">
              <ExcalidrawComponent initialData={excalidrawRef} viewMode={true} />
            </div>
          ) : (
            <div className="excalidraw-placeholder">
              No Excalidraw reference found
            </div>
          )}
        </ExcalidrawViewContainer>
      )}
    </ExcalidrawBlockContainer>
  );
});

ExcalidrawBlock.displayName = 'ExcalidrawBlock';

export default ExcalidrawBlock;
export { extractExcalidrawRef, createExcalidrawMarkdown };
